import React from 'react';
import * as ReactRouterDOM from 'react-router-dom';

interface OgData {
    title?: string;
    description?: string;
    type?: string;
    image?: string;
    url?: string;
}

interface PageMetadata {
    title: string;
    description: string;
    schema?: object | object[];
    ogData?: OgData;
}

// Helper to set or create a meta tag
const setMetaTag = (attrName: 'name' | 'property', attrValue: string, content: string) => {
    let element = document.querySelector(`meta[${attrName}='${attrValue}']`) as HTMLMetaElement | null;
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
};

// Helper to set or create the canonical link
const setCanonicalLink = (href: string) => {
    let element = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', 'canonical');
        document.head.appendChild(element);
    }
    element.setAttribute('href', href);
};

// Helper to add or update JSON-LD schema script tags
const setSchema = (schema: object | object[], id: string) => {
    let element = document.getElementById(id) as HTMLScriptElement | null;
    if (!element) {
        element = document.createElement('script');
        element.type = 'application/ld+json';
        element.id = id;
        document.head.appendChild(element);
    }
    element.innerHTML = JSON.stringify(schema);
};

export const usePageMetadata = ({ title, description, schema, ogData }: PageMetadata) => {
    const location = ReactRouterDOM.useLocation();

    React.useEffect(() => {
        const baseUrl = (window.location.origin).replace(/\/$/, '');

        let canonicalUrl;
        if (location.pathname === '/') {
            canonicalUrl = `${baseUrl}/`;
        } else {
            canonicalUrl = `${baseUrl}${location.pathname}`;
        }


        // Set Title
        if (title) {
            document.title = title;
        }

        // Set Description
        if (description) {
            setMetaTag('name', 'description', description);
        }

        // Set Canonical URL
        setCanonicalLink(canonicalUrl);

        // --- Open Graph & Twitter Card Meta Tags ---
        const defaultOgImage = `${baseUrl}/og-image.png`;
        const ogTitle = ogData?.title || title;
        const ogDescription = ogData?.description || description;
        const ogType = ogData?.type || 'website';
        const ogImage = ogData?.image || defaultOgImage;
        const ogUrl = ogData?.url || canonicalUrl;

        setMetaTag('property', 'og:title', ogTitle);
        setMetaTag('property', 'og:description', ogDescription);
        setMetaTag('property', 'og:type', ogType);
        setMetaTag('property', 'og:image', ogImage);
        setMetaTag('property', 'og:url', ogUrl);
        setMetaTag('property', 'og:site_name', 'BiznesBezKlikania.pl');

        setMetaTag('name', 'twitter:card', 'summary_large_image');
        setMetaTag('name', 'twitter:site', '@BartoszGaca');
        setMetaTag('name', 'twitter:title', ogTitle);
        setMetaTag('name', 'twitter:description', ogDescription);
        setMetaTag('name', 'twitter:image', ogImage);


        // Set Page-specific Schema
        const pageSchemaId = 'page-schema';
        if (schema) {
            setSchema(schema, pageSchemaId);
        } else {
            // Remove page-specific schema if not provided
            const existingSchema = document.getElementById(pageSchemaId);
            if (existingSchema) {
                existingSchema.remove();
            }
        }

    }, [title, description, location.pathname, schema, ogData]);
};